import { useMemo } from 'react';
import { GuideHeader } from './components/GuideHeader';
import { GoalSection, GoalType } from './components/GoalSection';
import { GuideDismissFooter } from './components/GuideDismissFooter';
import { GoLiveModal } from '../home/components/GoLiveModal';
import SuperTooltipRenderer from '@client/new/components/SuperTooltip/SuperTooltipRenderer';
import ChecklistTooltipRenderer from '@client/new/components/SuperTooltip/ChecklistTooltipRenderer';
import { useSuperTooltipVM } from '@client/new/components/SuperTooltip/super-tooltip.viewModel';
import { SuperTooltipTemplateId } from '@client/new/components/SuperTooltip/generated';
import { GuideVM } from './guide.viewModel';
import { Checklist } from './model';
import { getPagePath } from './utils/pageActionMap';
import { navigate } from '@client/new/helpers/history';
import * as styles from './Guide.view.css';

type TaskSuperTooltipModalProps = {
  templateId: string;
  influencerId: number;
  canGoLive: boolean;
  onGoLive: () => void;
  onClose: () => void;
};

const INTERNAL_LINK_PREFIX = 'internal:';

const TaskSuperTooltipModal = ({
  templateId,
  influencerId,
  canGoLive,
  onGoLive,
  onClose,
}: TaskSuperTooltipModalProps) => {
  const isChecklist = templateId.startsWith('checklist-');
  const vm = useSuperTooltipVM({
    templateId: templateId as SuperTooltipTemplateId,
    hideNavigation: isChecklist,
    showDivider: false,
  });

  const handleLinkClicked = (link: string) => {
    if (link.startsWith(INTERNAL_LINK_PREFIX)) {
      const action = link.slice(INTERNAL_LINK_PREFIX.length);

      if (action === 'go-live') {
        if (!canGoLive) return;
        onClose();
        onGoLive();
        return;
      }

      const path = getPagePath(action, influencerId);
      if (path) {
        onClose();
        navigate(path);
        return;
      }
    }

    vm.onLinkClicked(link);
  };

  if (vm.templates.length === 0) {
    onClose();
    return null;
  }

  if (isChecklist) {
    return (
      <ChecklistTooltipRenderer
        template={vm.templates[0]}
        isOpen={true}
        onClose={onClose}
        onLinkClicked={handleLinkClicked}
      />
    );
  }

  return <SuperTooltipRenderer {...vm} isOpen={true} onClose={onClose} />;
};

const isChecklistCompleted = (c: Checklist): boolean =>
  c.tasks.every((t) => t.isChecked);

const areAllChecklistsCompleted = (
  completedCount: number,
  totalCount: number,
): boolean => totalCount > 0 && completedCount === totalCount;

const getGoalType = (
  checklist: Checklist,
  index: number,
  activeIndex: number,
  allCompleted: boolean,
): GoalType => {
  if (allCompleted && activeIndex === -1 && index === 0) {
    return 'current';
  }
  if (index < activeIndex) return 'previous';
  if (checklist.isActive) return 'current';
  return 'next';
};

export const GuideView = (p: GuideVM) => {
  const originalIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    p.checklists.forEach((c, i) => map.set(c.id, i + 1));
    return map;
  }, [p.checklists]);

  const sortedChecklists = useMemo(() => {
    return [...p.checklists].sort((a, b) => {
      const aComplete = isChecklistCompleted(a);
      const bComplete = isChecklistCompleted(b);
      if (aComplete && !bComplete) return 1;
      if (!aComplete && bComplete) return -1;
      return 0;
    });
  }, [p.checklists]);

  const activeIndex = sortedChecklists.findIndex((c) => c.isActive);
  const allCompleted = areAllChecklistsCompleted(
    p.completedChecklistsCount,
    p.totalChecklistsCount,
  );

  return (
    <div className={styles.container}>
      <GuideHeader
        strategy={p.strategy}
        completedChecklistsCount={p.completedChecklistsCount}
        totalChecklistsCount={p.totalChecklistsCount}
        totalEstimatedTime={p.totalEstimatedTime}
        isLive={p.isLive}
        canGoLive={p.canGoLive}
        onGoLive={() => p.dispatch('openGoLiveModal')}
        onReview={() => p.dispatch('reviewStrategy')}
      />

      <div className={styles.goalsContainer}>
        {sortedChecklists.map((checklist, index) => (
          <div key={checklist.id}>
            {index > 0 && <div className={styles.goalDivider} />}
            <GoalSection
              checklist={checklist}
              checklistNumber={originalIndexMap.get(checklist.id) ?? index + 1}
              goalType={getGoalType(
                checklist,
                index,
                activeIndex,
                allCompleted,
              )}
              isOpening={p.openingChecklistId === checklist.id}
              isCollapsed={p.isCollapsed && checklist.isActive}
              isCompleted={isChecklistCompleted(checklist)}
              influencerId={p.influencerId}
              onOpen={(id) => p.dispatch('openChecklist', id)}
              onCollapse={() => p.dispatch('collapseActiveChecklist')}
              onTaskToggle={(taskId, completed) =>
                p.dispatch('toggleTask', { taskId, completed })
              }
              onOpenTaskModal={(templateId) =>
                p.dispatch('openTaskModal', templateId)
              }
            />
          </div>
        ))}
      </div>

      <GuideDismissFooter onDismiss={() => p.dispatch('dismiss')} />

      <GoLiveModal
        isOpen={p.goLiveModal.isOpen}
        onClose={p.goLiveModal.onClose}
        version="otp"
        goLive={p.goLiveModal.goLive}
        inProgress={p.goLiveModal.inProgress}
      />

      {p.taskModal.isOpen && p.taskModal.templateId && (
        <TaskSuperTooltipModal
          templateId={
            p.taskModal.templateId === 'checklist-go-live' && !p.canGoLive
              ? 'checklist-go-live-not-ready'
              : p.taskModal.templateId
          }
          influencerId={p.influencerId}
          canGoLive={p.canGoLive}
          onGoLive={() => p.dispatch('openGoLiveModal')}
          onClose={p.taskModal.onClose}
        />
      )}
    </div>
  );
};
